import { useState, useEffect } from "react";
import { useTheme } from '../../CommonComponents/ThemeProvider';
import FileUploadField from "../../CommonComponents/FileUploadField";
import tinycolor from "tinycolor2";

export default function AddProductModal({ isOpen, onClose, onSubmit, editData, branches = [] }) {
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme.color;
    const hoverColor = tinycolor(primaryColor).darken(10).toString();

    const [productData, setProductData] = useState({
        name: "",
        price: "",
        unit_type: "",
        category: "",
        description: "",
        image: null,
        initialQuantity: "",
        branch_id: "",
        branch_price: "",
    });

    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (editData) {
            setProductData({
                name: editData.name || "",
                price: editData.price || "",
                unit_type: editData.unit_type || "",
                category: editData.category || "",
                description: editData.description || "",
                image: null,
                initialQuantity: "",
                branch_id: editData.branch_id || "",
                branch_price: editData.branch_price || "",
            });
            setPreview(editData.image || null); // full URL from backend
        } else {
            setProductData({
                name: "",
                price: "",
                unit_type: "",
                category: "",
                description: "",
                image: null,
                initialQuantity: "",
                branch_id: "",
                branch_price: "",
            });
            setPreview(null);
        }
    }, [editData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProductData((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "category" && value === "Bakery Product" ? { initialQuantity: "" } : {}),
            ...(name === "branch_id" && value === "" ? { branch_price: "" } : {}),
        }));
    };

    const handleImageChange = (file) => {
        setProductData({ ...productData, image: file });
        setPreview(file ? URL.createObjectURL(file) : null);
    };

    const handleImageRemove = () => {
        setProductData({ ...productData, image: null });
        setPreview(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("name", productData.name);
        formData.append("price", productData.price);
        formData.append("unit_type", productData.unit_type);
        formData.append("category", productData.category);
        formData.append("description", productData.description);
        if (productData.branch_id) {
            formData.append("branch_id", productData.branch_id);
        }
        if (productData.branch_price !== "") {
            formData.append("branch_price", productData.branch_price);
        }
        if (editData?.previous_branch_id) {
            formData.append("previous_branch_id", editData.previous_branch_id);
        }
        if (!editData) {
            formData.append("initialQuantity", productData.initialQuantity === "" ? "0" : String(productData.initialQuantity));
        }

        if (productData.image) {
            formData.append("image", productData.image);
        }

        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
                <h2 className="text-xl font-bold mb-4">{editData ? "Edit Product" : "Add Product"}</h2>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                    <input name="name" placeholder="Product Name" value={productData.name} onChange={handleChange} className="border px-2 py-1 rounded" required />
                    <input name="price" type="number" placeholder="Universal Price" value={productData.price} onChange={handleChange} className="border px-2 py-1 rounded" min="0" step="0.01" required />
                    <select name="unit_type" value={productData.unit_type} onChange={handleChange} className="border px-2 py-1 rounded" required>
                        <option value="">Select unit type</option>
                        <option value="pcs">Piece</option>
                        <option value="packs">Packs</option>
                        <option value="socks">Socks</option>
                    </select>
                    <select name="category" value={productData.category} onChange={handleChange} className="border px-2 py-1 rounded" required>
                        <option value="">Select category</option>
                        <option value="Bakery Product">Bakery Product</option>
                        <option value="Grocery Product">Grocery Product</option>
                    </select>
                    <select
                        name="branch_id"
                        value={productData.branch_id}
                        onChange={handleChange}
                        className="border px-2 py-1 rounded"
                    >
                        <option value="">All branches (universal price only)</option>
                        {branches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                                {branch.name}
                            </option>
                        ))}
                    </select>
                    {productData.branch_id && (
                        <input
                            name="branch_price"
                            type="number"
                            placeholder="Branch Price (optional)"
                            value={productData.branch_price}
                            onChange={handleChange}
                            className="border px-2 py-1 rounded"
                            min="0"
                            step="0.01"
                        />
                    )}
                    <textarea name="description" placeholder="Product Description" value={productData.description} onChange={handleChange} className="border px-2 py-1 rounded resize-none" rows={3} />
                    {!editData && productData.category !== "Bakery Product" && (
                        <input
                            name="initialQuantity"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Initial Stocks Available (Admin)"
                            value={productData.initialQuantity}
                            onChange={handleChange}
                            className="border px-2 py-1 rounded"
                        />
                    )}
                    <FileUploadField
                        label="Product Image"
                        accept="image/*"
                        value={productData.image}
                        preview={preview}
                        onChange={handleImageChange}
                        onRemove={handleImageRemove}
                        optional={true}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <button type="button" className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400" onClick={onClose}>Cancel</button>
                        <button
                            type="submit"
                            className="px-3 py-1 text-white rounded font-medium shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: primaryColor }}
                            onMouseOver={e => e.currentTarget.style.background = hoverColor}
                            onMouseOut={e => e.currentTarget.style.background = primaryColor}
                        >
                            {editData ? "Update" : "Add"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
